import { Injectable, inject, signal } from "@angular/core";
import { Location } from "@angular/common";
import { BehaviorSubject, Subject } from "rxjs";
import { NavigationBarItem } from "./bootstrap-components/navigation-bar/navigation-bar-item";
import { ApplicationActionsConfig } from "./common/application-actions-config";
import theme from "devextreme/ui/themes";
import { setFavicon } from "src/app/support/favicon";
import { Nullable } from "src/app/support/nullable";
import { MemberAuth } from "src/app/backend/models/member/member-auth";
import { User } from "src/app/backend/models/user/user";
import { Box } from "./rocket-ui/backend/models/box";
import { Tenant } from "src/app/backend/models/tenant/tenant";
import { first, split, toLower } from "lodash-es";
import { NavigatedRoute } from "./bootstrap-components/navigation-stack-bar/navigated-route";
import { Race } from "src/app/backend/models/race/race";
import { Configuration } from "src/app/backend/models/configuration";
import { Member } from "src/app/backend/models/member/member";
import { ProductService } from "src/app/services/product.service";
import { BillingConfiguration } from "src/app/backend/models/billing-configuration";
import { AppointmentConfiguration } from "src/app/backend/models/appointment-configuration";
import { HealthcareConfiguration } from "src/app/backend/models/healthcare-configuration";
import { OrganizationConfiguration } from "src/app/backend/models/organization-configuration";
import { appState } from "src/app/core/state";
import { LicenseService } from "src/app/services/feature-access-control/license.service";
import * as Sentry from "@sentry/angular";
import { MemberStorageService } from "src/app/services/member-storage.service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { OpenPanelService } from "src/app/services/openpanel.service";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { SoftwareProduct } from "src/app/support/product";
import { NavigationBarMode } from "src/app/modules/bootstrap/bootstrap-components/navigation-bar/navigation-bar.component";

type ThemeType = "light" | "dark";

declare global {
  interface Window {
    config: any;
  }
}

@Injectable()
export class BootstrapService {
  static instance: BootstrapService;

  productTitle$ = new BehaviorSubject<string>("");
  menu$ = new BehaviorSubject<NavigationBarItem[]>([]);
  settingsMenu$ = new BehaviorSubject<NavigationBarItem[]>([]);

  readonly navigationBarMode = signal<NavigationBarMode>(
    NavigationBarMode.FLOATING
  );

  applicationLoad$ = new Subject<void>();

  applicationsActionsHolder: Record<
    string,
    Record<string, ApplicationActionsConfig>
  > = {};

  private currentTab: Nullable<NavigatedRoute>;
  private tabCache: Record<string, any> = {};

  private currentUser: Nullable<User> = null;
  private currentMemberAuth: Nullable<MemberAuth> = null;
  private currentFacilityCode: string | null = null;
  private currentBox: Nullable<Box>;
  private currentTenant: Nullable<Tenant>;
  private currentConfiguration: Configuration | undefined;
  private currentBillingConfiguration: BillingConfiguration | undefined;

  private currentAppointmentConfiguration: AppointmentConfiguration | undefined;
  private currentHealthcareConfiguration: HealthcareConfiguration | undefined;
  private currentOrganizationConfiguration:
    | OrganizationConfiguration
    | undefined;

  private allMembers:
    | Array<{
        member: MemberAuth;
        tenant: Tenant;
      }>
    | undefined;

  private availableMembersAndTenants:
    | Array<{
        member: MemberAuth;
        tenant: Tenant;
      }>
    | undefined;

  private breed: Nullable<Race>;

  private readonly licenseService = inject(LicenseService);
  private readonly location = inject(Location);
  private readonly productService = inject(ProductService);
  private readonly memberStorageService = inject(MemberStorageService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly openPanelService = inject(OpenPanelService);

  constructor() {
    BootstrapService.instance = this;
  }

  getAllMembers():
    | Array<{
        member: MemberAuth;
        tenant: Tenant;
      }>
    | undefined {
    return this.allMembers;
  }

  setAllMembers(
    value: Array<{
      member: MemberAuth;
      tenant: Tenant;
    }>
  ): void {
    this.allMembers = value;
  }

  setAvailableMembersAndTenants(
    value: Array<{
      member: MemberAuth;
      tenant: Tenant;
    }>
  ): void {
    this.availableMembersAndTenants = value;
  }

  getAvailableMembersAndTenants():
    | Array<{
        member: MemberAuth;
        tenant: Tenant;
      }>
    | undefined {
    return this.availableMembersAndTenants;
  }

  getBreed(): Nullable<Race> {
    return this.breed;
  }

  setBreed(breed: Race): Nullable<Race> {
    return (this.breed = breed);
  }

  setCurrentConfiguration(configuration: Configuration): void {
    this.currentConfiguration = configuration;
  }

  getCurrentConfiguration(): Configuration | null {
    return this.currentConfiguration!;
  }

  setCurrentBillingConfiguration(
    billingConfiguration: BillingConfiguration
  ): void {
    this.currentBillingConfiguration = billingConfiguration;
  }

  getCurrentBillingConfiguration(): BillingConfiguration {
    return this.currentBillingConfiguration!;
  }

  setCurrentAppointmentConfiguration(
    appointmentConfiguration: AppointmentConfiguration
  ): void {
    this.currentAppointmentConfiguration = appointmentConfiguration;
  }

  getCurrentAppointmentConfiguration(): AppointmentConfiguration {
    return this.currentAppointmentConfiguration;
  }

  setCurrentHealthcareConfiguration(
    healthcareConfiguration: HealthcareConfiguration
  ): void {
    this.currentHealthcareConfiguration = healthcareConfiguration;
  }

  getCurrentHealthcareConfiguration(): HealthcareConfiguration | undefined {
    return this.currentHealthcareConfiguration;
  }

  setCurrentOrganizationConfiguration(
    organizationConfiguration: OrganizationConfiguration
  ): void {
    this.currentOrganizationConfiguration = organizationConfiguration;
  }

  getCurrentOrganizationConfiguration(): OrganizationConfiguration | undefined {
    return this.currentOrganizationConfiguration;
  }

  getCurrentTenant(): Nullable<Tenant> {
    return this.currentTenant;
  }

  setCurrentTenant(tenant: Tenant): void {
    this.licenseService.set(tenant.plan.id);
    this.currentTenant = tenant;
    appState.tenantId = tenant.id;

    this.localStorageService.setItem("tenant-id", tenant.id);

    Sentry.setTags({
      tenant: tenant.id,
      tenantName: tenant.name
    });

    this.syncOpenPanelIdentity();
  }

  getCurrentBox(): Nullable<Box> {
    return this.currentBox;
  }

  setCurrentBox(box: Box): void {
    this.currentBox = box;
  }

  setCurrentUser(user: User): void {
    this.currentUser = user;
  }

  getCurrentUser(): Nullable<User> {
    return this.currentUser;
  }

  setCurrentMemberAuth(member: MemberAuth | null): void {
    this.currentMemberAuth = member;

    this.localStorageService.setItem("member-id", member?.id || "");
  }

  getCurrentMemberAuth(): Nullable<MemberAuth> {
    return this.currentMemberAuth;
  }

  setCurrentMember(member: Member): void {
    appState.member = member;

    this.syncOpenPanelIdentity();
  }

  getCurrentMember(): Nullable<Member> {
    return appState.member;
  }

  loadCurrentTheme(): void {
    const themeType: ThemeType =
      <ThemeType>this.memberStorageService.getItem("theme") || "light";
    this.changeCurrentTheme(themeType);
  }

  changeCurrentTheme(themeType: ThemeType): void {
    this.memberStorageService.setItem("theme", themeType);

    //theme.current(themeType === "light" ? "generic.light" : "generic.dark");
    theme.current(
      themeType === "light" ? "fluent.saas.light.compact" : "generic.dark"
    );

    if (themeType === "light") {
      document.body.classList.remove("dark");
      document.body.classList.remove("sl-theme-dark");
    } else {
      document.body.classList.add("dark");
      document.body.classList.add("sl-theme-dark");
    }

    const product = this.getCurrentProduct();

    switch (product) {
      case "Rocket": {
        setFavicon(`/assets/favicons/rocket-${themeType}.png`);
        break;
      }
      case "PMS": {
        setFavicon(`/assets/favicons/pms-${themeType}.png`);
        break;
      }
    }
  }

  getCurrentTheme(): ThemeType {
    return <ThemeType>(this.memberStorageService.getItem("theme") || "light");
  }

  getApplicationNameByDomain(): string {
    return toLower(first(split(location.host, ".")));
  }

  getCurrentProduct(): SoftwareProduct {
    return this.productService.getCurrentProduct();
  }

  getCurrentTab(): Nullable<NavigatedRoute> {
    return this.currentTab;
  }

  setCurrentTab(tab: NavigatedRoute): void {
    this.currentTab = tab;
  }

  setCurrentTabDead(): void {
    if (this.currentTab) {
      this.currentTab.dead = true;
    }
  }

  async closeCurrentTab(): Promise<void> {
    this.setCurrentTabDead();
    await this.location.back();
  }

  getItemFromTabCache<T>(key: string): T {
    return this.tabCache[key];
  }

  putItemInTabCache<T>(key: string, item: T): void {
    this.tabCache[key] = item;
  }

  setProductTitle(title: string): void {
    this.productTitle$.next(title);
  }

  setCurrentFacilityCode(facilityCode: string): void {
    this.currentFacilityCode = facilityCode;
  }

  getCurrentFacilityCode(): string {
    return this.currentFacilityCode!;
  }

  async setMenu(menu: NavigationBarItem[]): Promise<void> {
    this.menu$.next(menu);
  }

  async setSettingsMenu(menu: NavigationBarItem[]): Promise<void> {
    this.settingsMenu$.next(menu);
  }

  async provideApplicationActions(
    moduleName: string,
    extraName: string,
    config: ApplicationActionsConfig
  ): Promise<void> {
    if (!this.applicationsActionsHolder[moduleName]) {
      this.applicationsActionsHolder[moduleName] = {};
    }

    this.applicationsActionsHolder[moduleName][extraName] = config;
  }

  async getApplicationActions(
    moduleName: string,
    extraName: string
  ): Promise<ApplicationActionsConfig | null> {
    const moduleConfig = this.applicationsActionsHolder[moduleName];
    return moduleConfig ? moduleConfig[extraName] : null;
  }

  async backOrDefault(fnDefault: () => Promise<any>): Promise<void> {
    if (document.referrer) {
      this.location.back();
    } else {
      await fnDefault();
    }
  }

  private syncOpenPanelIdentity(): void {
    const member: Member | null = this.getCurrentMember();
    const tenant: Tenant | null = this.getCurrentTenant();

    if (!member || !tenant) {
      return;
    }

    this.openPanelService.identifyMember(member, tenant);
  }

  updateActiveStatusByUrl(menuItems: NavigationBarItem[], url: string): void {
    menuItems.forEach((item) => {
      if (item.items && item.items.length > 0) {
        let hasActiveChild = false;

        item.items.forEach((childItem) => {
          childItem.active = childItem.url
            ? url.startsWith(childItem.url)
            : false;

          if (childItem.active) {
            hasActiveChild = true;
          }
        });

        item.active = hasActiveChild;

        if (
          url.indexOf("/organization/invoices") >= 0 &&
          item.title === sntTranslate(marker("Fatturazione"))
        ) {
          item.active = true;
        }

        this.updateActiveStatusByUrl(item.items, url);
      } else {
        item.active = item.url ? url.startsWith(item.url) : false;
      }
    });
  }
}
